import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import {
  COMPANY,
  CASES,
  TIMELINE_SHORT,
  SERVICES_SHORT,
  FOUNDER,
  CLIENTS,
  PROOF_STATS,
  DIVISIONS,
} from "@/data/content";

function CaseBlock({ c, index }) {
  return (
    <article
      data-testid={`case-${c.slug}`}
      className="case-block grid md:grid-cols-12 gap-8 md:gap-12 items-center border-t border-line py-16 md:py-24 first:border-t-0 first:pt-0"
    >
      <div className={`md:col-span-7 ${index % 2 === 1 ? "md:order-2" : ""}`}>
        <div className="relative aspect-[5/4] overflow-hidden bg-mute">
          <img
            src={c.image}
            alt={c.imageAlt}
            referrerPolicy="no-referrer"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </div>
      <div className={`md:col-span-5 ${index % 2 === 1 ? "md:order-1 md:pr-8" : "md:pl-4"}`}>
        <div className="eyebrow mb-4">{String(index + 1).padStart(2, "0")} · {c.role}</div>
        <h3
          className="display text-ink"
          style={{ fontSize: "clamp(32px, 4vw, 64px)" }}
        >
          {c.title}
        </h3>
        <p className="mt-6 text-ink-800 leading-relaxed text-base md:text-lg">{c.summary}</p>

        <div className="mt-8 grid grid-cols-3 gap-px bg-line border border-line">
          {c.metrics.map((m, i) => (
            <div key={i} className="bg-paper p-4 md:p-5">
              <div className="display text-ink text-xl md:text-2xl leading-tight" style={{ letterSpacing: "-0.02em" }}>{m.v}</div>
              <div className="eyebrow text-warm-600 mt-2" style={{ fontSize: "10px" }}>{m.l}</div>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page" className="relative">
      {/* HERO — Clean, editorial, no top-bar clutter. */}
      <section className="relative pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14">
          <Reveal>
            <div className="mb-10 md:mb-14 flex flex-wrap items-baseline gap-x-6 gap-y-2">
              <div className="eyebrow text-ink" style={{ letterSpacing: "0.24em" }}>
                Lennon Management
              </div>
              <div className="eyebrow text-warm-600" style={{ letterSpacing: "0.24em" }}>
                Est. {COMPANY.established}
              </div>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <h1
              data-testid="hero-headline"
              className="display text-ink"
              style={{
                fontSize: "clamp(52px, 10vw, 200px)",
                lineHeight: 0.92,
                letterSpacing: "-0.04em",
              }}
            >
              {COMPANY.hero.line1}
              <br />
              {COMPANY.hero.line2}
              <br />
              <span className="text-warm-600">{COMPANY.hero.line3}</span>
            </h1>
          </Reveal>

          <Reveal delay={150}>
            <div className="grid md:grid-cols-12 gap-10 md:gap-16 mt-16 md:mt-20 items-start">
              <div className="md:col-span-7">
                <p
                  className="text-ink-800 text-xl md:text-2xl leading-snug max-w-2xl"
                  style={{ letterSpacing: "-0.01em" }}
                >
                  {COMPANY.tagline}
                </p>
                <div className="mt-10">
                  <a
                    href={COMPANY.companiesUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="hero-cta-companies"
                    className="inline-flex items-center gap-2 text-sm text-warm-600 hover:text-ink border-b border-transparent hover:border-warm-600 pb-1 transition-colors"
                  >
                    For companies → Lennon Global <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>

              {/* Founder card — quiet, right-aligned */}
              <div className="md:col-span-5">
                <div className="relative aspect-[4/5] overflow-hidden bg-mute">
                  <img
                    src={FOUNDER.image}
                    alt={FOUNDER.name}
                    loading="eager"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: "center top" }}
                  />
                </div>
                <div className="mt-4 flex items-baseline justify-between">
                  <div className="display text-ink text-xl">{FOUNDER.name}</div>
                  <div className="eyebrow">{FOUNDER.title}</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROOF STATS */}
      <section data-testid="home-proof" className="border-t border-line py-16 md:py-20 bg-mute">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14">
          <Reveal>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line">
              {PROOF_STATS.map((s) => (
                <li key={s.l} className="bg-paper px-8 py-10 md:py-14">
                  <div
                    className="display text-ink"
                    style={{ fontSize: "clamp(44px, 5.5vw, 88px)", letterSpacing: "-0.03em", lineHeight: 1 }}
                  >
                    {s.v}
                  </div>
                  <div className="eyebrow text-warm-600 mt-4">{s.l}</div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ONE BRAND. TWO DIVISIONS. — consolidated. Single CTA. */}
      <section
        data-testid="home-divisions"
        className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-t border-line"
      >
        <Reveal>
          <h2
            className="display text-ink"
            style={{ fontSize: "clamp(36px, 5vw, 88px)", letterSpacing: "-0.03em", lineHeight: 0.98 }}
          >
            One brand.<br />
            <span className="text-warm-600">Two divisions.</span>
          </h2>
        </Reveal>

        <Reveal delay={100}>
          <ul className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
            {DIVISIONS.map((d) => (
              <li
                key={d.key}
                data-testid={`division-${d.key}`}
                className="p-10 md:p-14 flex flex-col"
                style={d.isCurrent ? { backgroundColor: "#f7f5f0" } : { backgroundColor: "#0a0a0a", color: "#fff" }}
              >
                <div
                  className={`eyebrow mb-4 ${d.isCurrent ? "text-warm-600" : "text-warm-400"}`}
                  style={{ letterSpacing: "0.24em" }}
                >
                  {d.tag}
                </div>
                <h3
                  className={`display ${d.isCurrent ? "text-ink" : "text-paper"}`}
                  style={{ fontSize: "clamp(28px, 3.4vw, 48px)", letterSpacing: "-0.03em", lineHeight: 1 }}
                >
                  {d.name}
                </h3>
                <p
                  className={`mt-6 text-base md:text-lg leading-relaxed max-w-md ${d.isCurrent ? "text-ink-800" : "text-warm-400"}`}
                >
                  {d.lead}
                </p>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={200}>
          <div className="mt-10 md:mt-14 flex flex-wrap items-center gap-8 md:gap-10">
            <a
              href={COMPANY.companiesUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="home-divisions-global-cta"
              className="inline-flex items-center gap-2 text-sm text-ink hover:text-warm-600 border-b border-ink/40 hover:border-warm-600 pb-1"
            >
              Visit Lennon Global <ArrowUpRight size={14} />
            </a>
          </div>
        </Reveal>
      </section>

      {/* THE DIFFERENCE — short philosophy of method, not comparison. */}
      <section
        data-testid="home-difference"
        className="bg-ink text-paper py-24 md:py-32 border-t border-line"
      >
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14">
          <Reveal>
            <div className="eyebrow text-warm-400 mb-10 md:mb-14" style={{ letterSpacing: "0.24em" }}>
              The difference
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h2
              className="display text-paper"
              style={{ fontSize: "clamp(44px, 8vw, 160px)", lineHeight: 0.94, letterSpacing: "-0.04em" }}
            >
              Built differently.
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p
              className="mt-12 md:mt-16 max-w-3xl text-warm-400 leading-snug"
              style={{ fontSize: "clamp(22px, 2.4vw, 36px)", letterSpacing: "-0.01em" }}
            >
              Creators are not campaigns to manage.<br />
              They&rsquo;re businesses to build.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CREATOR WORK — the strongest section. Kept intact. */}
      <section data-testid="home-cases" className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-t border-line">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <div className="eyebrow mb-4">Selected work</div>
              <h2 className="display text-ink" style={{ fontSize: "clamp(40px, 6vw, 96px)", letterSpacing: "-0.03em" }}>
                Creator businesses,<br/>built.
              </h2>
            </div>
            <Link to="/work" data-testid="home-cases-cta" className="inline-flex items-center gap-3 text-sm text-ink hover:text-warm-600 border-b border-ink/40 hover:border-warm-600 pb-1">
              View selected work <ArrowUpRight size={14} />
            </Link>
          </div>
        </Reveal>

        <div>
          {CASES.slice(0, 3).map((c, i) => (
            <Reveal key={c.slug} delay={i * 60}>
              <CaseBlock c={c} index={i} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHAT WE DO */}
      <section data-testid="home-services" className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-t border-line">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <Reveal className="md:col-span-5">
            <div className="eyebrow mb-4">What we do</div>
            <h2 className="display text-ink" style={{ fontSize: "clamp(40px, 6vw, 96px)", letterSpacing: "-0.03em" }}>
              Relationship first.
            </h2>
            <Link
              to="/creators"
              data-testid="home-services-cta"
              className="mt-8 inline-flex items-center gap-3 text-sm text-ink hover:text-warm-600 border-b border-ink/40 hover:border-warm-600 pb-1"
            >
              All services <ArrowUpRight size={14} />
            </Link>
          </Reveal>
          <div className="md:col-span-7 md:pt-6 space-y-12">
            {SERVICES_SHORT.map((s, i) => (
              <Reveal key={s.n} delay={i * 80}>
                <div className="grid grid-cols-12 gap-6 border-t border-line pt-6">
                  <div className="col-span-2 md:col-span-1 eyebrow text-warm-600">— {s.n}</div>
                  <div className="col-span-10 md:col-span-11">
                    <h3 className="display text-ink" style={{ fontSize: "clamp(24px, 2.6vw, 36px)" }}>{s.t}</h3>
                    <p className="mt-3 text-ink-800 leading-relaxed text-lg max-w-2xl">{s.b}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SELECTED EXPERIENCE — logos, no big display heading. */}
      <section data-testid="home-clients" className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-t border-line">
        <Reveal>
          <div className="mb-12 md:mb-16">
            <div className="eyebrow mb-4">Selected experience</div>
            <p className="text-warm-600 text-base md:text-lg max-w-2xl leading-relaxed">
              Brands, platforms and publishers across 15+ years of creator partnerships.
            </p>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-line border-t border-line">
            {CLIENTS.map((name) => (
              <li
                key={name}
                data-testid={`client-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-paper px-4 py-6 md:py-8 flex items-center justify-center text-center"
              >
                <span className="display text-ink text-lg md:text-xl leading-tight" style={{ letterSpacing: "-0.01em" }}>
                  {name}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* HISTORY — 4 anchors only. Full timeline lives on /about. */}
      <section data-testid="home-timeline" className="bg-ink text-paper py-24 md:py-32">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14">
          <Reveal>
            <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
              <div className="md:col-span-7">
                <div className="eyebrow text-warm-400 mb-4">In the room since 2009</div>
                <h2 className="display text-paper" style={{ fontSize: "clamp(40px, 6vw, 96px)", letterSpacing: "-0.03em" }}>
                  A career arc,<br/>not a positioning<br/>statement.
                </h2>
              </div>
              <div className="md:col-span-5 text-warm-400 text-lg leading-relaxed">
                The creator economy didn&rsquo;t arrive in 2024. It was built —
                slowly, deal by deal — by people who were paying attention in 2009.
              </div>
            </div>
          </Reveal>

          <ol className="border-t border-paper/15">
            {TIMELINE_SHORT.map((t, i) => (
              <Reveal as="li" key={t.year} delay={i * 60}>
                <div className="grid grid-cols-12 gap-6 md:gap-10 border-b border-paper/15 py-8 md:py-12 items-start">
                  <div className="col-span-3 md:col-span-2 display text-paper" style={{ fontSize: "clamp(22px, 2.8vw, 44px)", letterSpacing: "-0.02em" }}>
                    {t.year}
                  </div>
                  <div className="col-span-9 md:col-span-4 display text-paper" style={{ fontSize: "clamp(20px, 2.4vw, 32px)" }}>
                    {t.title}
                  </div>
                  <div className="col-span-12 md:col-span-6 text-warm-400 text-base md:text-lg leading-relaxed">
                    {t.body}
                  </div>
                </div>
              </Reveal>
            ))}
          </ol>

          <Reveal delay={200}>
            <div className="mt-14 md:mt-16">
              <Link to="/about" data-testid="home-timeline-cta" className="inline-flex items-center gap-2 text-sm text-paper hover:text-warm-400 border-b border-paper/40 hover:border-warm-400 pb-1">
                Full history →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT handled in Footer — single Let's talk closer. */}
    </div>
  );
}
